import { Button } from '@progress/kendo-react-buttons';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cancelGlobalSearch, loadGlobalSearchPage } from '../../store/application/types';
import styles from './_global-search.module.scss';
import { SvgIcon } from '@progress/kendo-react-common';
import { xIcon } from '@progress/kendo-svg-icons';
import { AppState } from '../../store/rootReducer';
import { hasValue, LoadingPanel } from '@amsanalytics/react-shared-library';
import { PageChangeEvent, Pager } from '@progress/kendo-react-data-tools';
import { Card, CardBody } from '@progress/kendo-react-layout';
import { Chip, Tooltip } from '@mui/material';
import { capitalizeFirstLetterOfEachWord } from '../../utils/utils';

// Returns the messages for the pager
const getPagerMessageMap = (messageKey: string, totalCount: number) => {
  switch (messageKey) {
    case 'pager.info':
      return {
        defaultMessage: '',
        messageKey: 'pager.info',
      };
    case 'pager.itemsPerPage':
      return {
        defaultMessage: `${totalCount.toLocaleString()} total`,
        messageKey: 'pager.itemsPerPage',
      };
    default:
      return {
        defaultMessage: '',
        messageKey: '',
      };
  }
};

export default function GlobalSearch() {
  // For dispatching actions
  const dispatch = useDispatch();
  const loadingGlobalSearchPage = useSelector((state: AppState) => state.applications.loadingGlobalSearchPage);
  const globalSearchText = useSelector((state: AppState) => state.applications.globalSearchText);
  const globalSearchPage = useSelector((state: AppState) => state.applications.globalSearchPage);
  const [pageState, setPageState] = useState({ skip: 0, take: 20 });

  // Returns true if there are NO search results for a user's search inqury.
  const noResultsForSearch = (): boolean => {
    return loadingGlobalSearchPage !== true && hasValue(globalSearchText) && globalSearchPage.count < 1;
  };

  return (
    <div className={styles.globalSearchContainer}>
      <div className={styles.searchSummaryRow}>
        {!hasValue(globalSearchText) && <div className={styles.searchText}>Type to search for applications...</div>}
        <div style={{ color: '#0061afcf' }}>
          {hasValue(globalSearchText) && (
            <>
              <span>Searching for applications</span>
              {hasValue(globalSearchText) && (
                <span>
                  &nbsp;by:&nbsp;
                  <span style={{ fontWeight: 700 }}>{globalSearchText}</span>
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <Button
        onClick={() => {
          dispatch(cancelGlobalSearch());
        }}
        themeColor='primary'
        fillMode='outline'
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          <SvgIcon icon={xIcon} themeColor='primary' />
          <span>Close</span>
        </div>
      </Button>
      <div className={styles.globalSearchResults}>
        {noResultsForSearch() !== true &&
          globalSearchPage.applications
            .sort((a, b) => b.applicationNo - a.applicationNo)
            .map((application, index) => {
              return (
                <Card
                  key={index}
                  onClick={() => {
                    window.open(`/order/${application.applicationId}`, '_blank');
                  }}
                  className={styles.searchItemCard}
                >
                  <CardBody className={styles.searchItem}>
                    <div className={styles.column}>
                      <div className={styles.columnHeader}>Application # {application.applicationNo}</div>
                      {application.appStatus && (
                        <Tooltip title={application.appStatus}>
                          <Chip
                            label={application.appStatus}
                            variant='outlined'
                            style={{
                              backgroundColor: application.statusBackgroundColor ?? 'inherit',
                              color: application.statusTextColor ?? 'inherit',
                              borderColor: application.statusBackgroundColor ?? 'inherit',
                              width: 'fit-content',
                              height: 'fit-content',
                              fontSize: '14px',
                              lineHeight: '20px',
                              borderRadius: '10px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'inline-block',
                              maxWidth: '100%',
                            }}
                          />
                        </Tooltip>
                      )}
                    </div>
                    {application.applicantName && (
                      <div className={styles.column}>
                        <div className={styles.columnHeader}>Applicant</div> <div>{application.applicantName}</div>
                      </div>
                    )}
                    {application.coApplicantName && (
                      <div className={styles.column}>
                        <div className={styles.columnHeader}>Co-Applicant</div> <div>{application.coApplicantName}</div>
                      </div>
                    )}
                    {application.location && (
                      <div className={styles.locationColumn}>
                        <div className={styles.columnHeader}>Location</div>
                        <Tooltip title={`Location: ${capitalizeFirstLetterOfEachWord(application.location)}`}>
                          <div className={styles.locationName}>{capitalizeFirstLetterOfEachWord(application.location)}</div>
                        </Tooltip>
                      </div>
                    )}
                    {application.assignments && (
                      <div className={styles.column}>
                        <div className={styles.columnHeader}>Assignments</div>
                        <div className={styles.assignments}>
                          {application.assignments.map((assignment, index) => {
                            return (
                              <div key={index}>
                                {application.assignments.length > 1 && index === application.assignments.length - 1 && (
                                  <>&nbsp;</>
                                )}
                                {assignment.assigneeName} ({assignment.role}){index !== application.assignments.length - 1 && ','}
                              </div>
                            );
                          })}
                          {application.assignments.length === 0 && 'None'}
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              );
            })}
      </div>
      {loadingGlobalSearchPage && (
        <LoadingPanel
          label='Searching for applications...'
          style={{
            position: 'fixed',
            zIndex: 2,
            background: '#FFFFFF',
            opacity: 0.7,
          }}
        />
      )}
      {noResultsForSearch() && (
        <div className={styles.containerWithCenteredChild}>
          <h2 style={{ color: 'darkgray' }}>No applications were found for this search.</h2>
        </div>
      )}
      <Pager
        skip={pageState.skip}
        take={pageState.take}
        total={globalSearchPage.count}
        style={{ zIndex: 3000 }}
        messagesMap={(messageKey: string) => getPagerMessageMap(messageKey, globalSearchPage.count)}
        onPageChange={(event: PageChangeEvent) => {
          setPageState(event);
          // Get applications based on new pagination data.
          dispatch(
            loadGlobalSearchPage({
              searchText: globalSearchText,
              skip: event.skip,
              take: event.take,
            })
          );
        }}
        buttonCount={2}
        previousNext={true}
        type='numeric'
        pageSizes={[15, 20, 50]}
      />
    </div>
  );
}
